import { DateRangeType } from '@g17eco/types/common';
import { SurveyFilters } from '@g17eco/types/portfolio-benchmarking';
import { Filters as ExchangeFilters } from '@apps/portfolio-tracker/components/portfolio/exchange-view/exchange-filters';
import { Filters as PackUsageFilters } from '@apps/portfolio-tracker/components/portfolio/benchmarking/partials/BenchmarkingFilters';
import { SubmissionInsightsFilters } from '@apps/portfolio-tracker/components/portfolio/benchmarking/submission-insights/types';

const EXCHANGE_FILTERS_KEY = 'portfolioTracker_exchangeFilters';
const BENCHMARKING_FILTERS_KEY = 'portfolioTracker_benchmarkingFilters';
const PACK_USAGE_FILTERS_KEY = 'portfolioTracker_packUsageFilters';
const SUBMISSION_INSIGHTS_FILTERS_KEY = 'portfolioTracker_submissionInsightsFilters';
const SHARED_DATE_RANGE_KEY = 'portfolioTracker_sharedDateRange';

// Shared Date Range State (used across all benchmarking tabs)
interface SharedDateRangeState {
  dateRange: DateRangeType;
  timeRange: string | number;
}

/**
 * Save shared date range to session storage (used across all benchmarking tabs)
 * @param portfolioId - Portfolio ID for isolation
 * @param state - Date range state to save
 */
export const saveSharedDateRange = (
  portfolioId: string,
  state: SharedDateRangeState
): void => {
  try {
    const key = `${SHARED_DATE_RANGE_KEY}_${portfolioId}`;
    sessionStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save shared date range to session storage', error);
  }
};

/**
 * Load shared date range from session storage (used across all benchmarking tabs)
 * @param portfolioId - Portfolio ID for isolation
 * @returns Saved date range state or null if not found
 */
export const loadSharedDateRange = (
  portfolioId: string
): SharedDateRangeState | null => {
  try {
    const key = `${SHARED_DATE_RANGE_KEY}_${portfolioId}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);

    if (parsed.dateRange?.startDate) {
      parsed.dateRange.startDate = new Date(parsed.dateRange.startDate);
    }
    if (parsed.dateRange?.endDate) {
      parsed.dateRange.endDate = new Date(parsed.dateRange.endDate);
    }

    return parsed;
  } catch (error) {
    console.error('Failed to load shared date range from session storage', error);
    return null;
  }
};

// Exchange View Filters State (dateRange/timeRange handled by Context)
interface ExchangeFilterState {
  filters: ExchangeFilters;
  searchText: string;
}

/**
 * Save exchange view filters to session storage
 * @param portfolioId - Portfolio ID for isolation
 * @param state - Filter state to save
 */
export const saveExchangeFilters = (
  portfolioId: string,
  state: ExchangeFilterState
): void => {
  try {
    const key = `${EXCHANGE_FILTERS_KEY}_${portfolioId}`;
    sessionStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save exchange filters to session storage', error);
  }
};

/**
 * Load exchange view filters from session storage
 * @param portfolioId - Portfolio ID for isolation
 * @returns Saved filter state or null if not found
 */
export const loadExchangeFilters = (
  portfolioId: string
): ExchangeFilterState | null => {
  try {
    const key = `${EXCHANGE_FILTERS_KEY}_${portfolioId}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) {
      return null;
    }

    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to load exchange filters from session storage', error);
    return null;
  }
};

/**
 * Save benchmarking filters to session storage
 * @param portfolioId - Portfolio ID for isolation
 * @param filters - Survey filters to save (dateRange/timeRange optional as they're handled by context)
 */
export const saveBenchmarkingFilters = (
  portfolioId: string,
  filters: Partial<SurveyFilters>
): void => {
  try {
    const key = `${BENCHMARKING_FILTERS_KEY}_${portfolioId}`;
    sessionStorage.setItem(key, JSON.stringify(filters));
  } catch (error) {
    console.error('Failed to save benchmarking filters to session storage', error);
  }
};

/**
 * Load benchmarking filters from session storage
 * @param portfolioId - Portfolio ID for isolation
 * @returns Saved filters or null if not found
 */
export const loadBenchmarkingFilters = (
  portfolioId: string
): SurveyFilters | null => {
  try {
    const key = `${BENCHMARKING_FILTERS_KEY}_${portfolioId}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) {
      return null;
    }

    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to load benchmarking filters from session storage', error);
    return null;
  }
};

/**
 * Save pack usage filters to session storage
 * @param portfolioId - Portfolio ID for isolation
 * @param filters - Pack usage filters to save (dateRange/timeRange handled by context)
 */
export const savePackUsageFilters = (
  portfolioId: string,
  filters: PackUsageFilters
): void => {
  try {
    const key = `${PACK_USAGE_FILTERS_KEY}_${portfolioId}`;
    sessionStorage.setItem(key, JSON.stringify(filters));
  } catch (error) {
    console.error('Failed to save pack usage filters to session storage', error);
  }
};

/**
 * Load pack usage filters from session storage
 * @param portfolioId - Portfolio ID for isolation
 * @returns Saved filters or null if not found
 */
export const loadPackUsageFilters = (
  portfolioId: string
): PackUsageFilters | null => {
  try {
    const key = `${PACK_USAGE_FILTERS_KEY}_${portfolioId}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) {
      return null;
    }

    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to load pack usage filters from session storage', error);
    return null;
  }
};

/**
 * Save submission insights filters to session storage
 * @param portfolioId - Portfolio ID for isolation
 * @param filters - Submission insights filters to save (dateRange/timeRange optional as they're handled by context)
 */
export const saveSubmissionInsightsFilters = (
  portfolioId: string,
  filters: Partial<SubmissionInsightsFilters>
): void => {
  try {
    const key = `${SUBMISSION_INSIGHTS_FILTERS_KEY}_${portfolioId}`;
    sessionStorage.setItem(key, JSON.stringify(filters));
  } catch (error) {
    console.error('Failed to save submission insights filters to session storage', error);
  }
};

/**
 * Load submission insights filters from session storage
 * @param portfolioId - Portfolio ID for isolation
 * @returns Saved filters or null if not found
 */
export const loadSubmissionInsightsFilters = (
  portfolioId: string
): SubmissionInsightsFilters | null => {
  try {
    const key = `${SUBMISSION_INSIGHTS_FILTERS_KEY}_${portfolioId}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) {
      return null;
    }

    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to load submission insights filters from session storage', error);
    return null;
  }
};

/**
 * Clear all portfolio filters from session storage
 * @param portfolioId - Portfolio ID to clear filters for
 */
export const clearPortfolioFilters = (portfolioId: string): void => {
  try {
    sessionStorage.removeItem(`${EXCHANGE_FILTERS_KEY}_${portfolioId}`);
    sessionStorage.removeItem(`${BENCHMARKING_FILTERS_KEY}_${portfolioId}`);
    sessionStorage.removeItem(`${PACK_USAGE_FILTERS_KEY}_${portfolioId}`);
    sessionStorage.removeItem(`${SUBMISSION_INSIGHTS_FILTERS_KEY}_${portfolioId}`);
    sessionStorage.removeItem(`${SHARED_DATE_RANGE_KEY}_${portfolioId}`);
  } catch (error) {
    console.error('Failed to clear portfolio filters from session storage', error);
  }
};
